// ---------------------------------------------------------------------------
// Moderating a person, mid-game, by name.
//
// **This exists because turning the chat off took kicking and banning with it.**
// Every moderation control in this app hung off a person's ROW — the lobby's
// roster, or a message in the chat panel — and both of those are gone once a
// game is running with `chatMode: 'off'`. So the one setting a host reaches for
// when a room turns nasty was also the setting that removed their ability to do
// anything about the person who caused it. That is the wrong way round, and it
// is the sort of gap that only shows up in the situation you least want it in.
//
// **A search field rather than a list, because the list is the problem.** With
// the seat cap gone a room can hold five hundred people, and a host who needs to
// remove one of them knows their name and does not want to scroll. Typing is the
// fastest path from "that person" to "gone", so the field is focused on arrival
// and the roster below it is a preview of what the typing is narrowing.
//
// **Nothing here is a seat change.** `assign-role` is lobby-only in the engine,
// so seating and benching are deliberately absent: a control that is refused
// every time it is pressed is worse than no control. What is here works in every
// phase — mute, kick, ban, and purge — and purge works on somebody who has
// already gone, which is the case it mostly exists for.
// ---------------------------------------------------------------------------

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { ClientGameState, ClientMessage, PublicPlayer } from '@countdown/shared';
import { Icon } from '../../../components/primitives/Icon.js';
import { BanIcon, KickIcon, MuteIcon, TrashIcon } from '../../../components/primitives/Icon.js';

/**
 * Case- and whitespace-insensitive, and a SUBSTRING rather than a prefix.
 *
 * A prefix match is the tidier rule and the wrong one here: display names are
 * suffixed on collision (`Isaac (2)`), people put brackets and tags around them,
 * and a host hunting for somebody mid-round will type the distinctive middle of
 * a name rather than its first letter. Nothing is being ranked, so there is no
 * cost to matching widely.
 */
function matches(player: PublicPlayer, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  if (player.displayName.toLowerCase().includes(q)) return true;
  return player.username !== null && player.username.toLowerCase().includes(q);
}

/**
 * Everybody a host could act on, seats and bench together, bots excluded.
 *
 * Bots are excluded because none of these four controls means anything to one:
 * a bot has no chat to purge, nothing to mute, and is removed from the lobby
 * with `remove-bot` rather than kicked. The host themselves is excluded for the
 * obvious reason.
 */
function moderatable(state: ClientGameState): PublicPlayer[] {
  return state.players.filter((player) => !player.isBot && player.id !== state.youId);
}

export function SafetyPlayers({
  state,
  send,
}: {
  state: ClientGameState;
  send: (message: ClientMessage) => void;
}): ReactElement {
  const [query, setQuery] = useState('');
  // Keyed by player id and holding which of the two two-press actions is armed.
  // One at a time across the whole list: arming a ban on one person must not
  // leave a ban armed on somebody else four rows down, where the next tap lands
  // on a confirmed destructive action the host never asked for.
  const [confirming, setConfirming] = useState<{ id: string; action: 'ban' | 'purge' } | null>(null);

  const people = useMemo(() => moderatable(state), [state]);
  const shown = useMemo(() => people.filter((player) => matches(player, query)), [people, query]);

  const act = (message: ClientMessage) => () => {
    send(message);
    setConfirming(null);
  };

  const row = 'flex min-h-11 items-center gap-2 rounded-chip px-2.5 text-left text-[13px] font-semibold';

  return (
    <div className="flex flex-col gap-2.5" data-testid="safety-players">
      <label className="flex flex-col gap-1">
        <span className="text-[13px] font-semibold">Find a player</span>
        <input
          type="search"
          value={query}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type a name"
          aria-label="Find a player"
          // 16px and not 15: iOS zooms the whole page when a field under 16px
          // takes focus, and `fieldZoom.test.ts` parses the frontend for exactly
          // this. A host reaching for this mid-round does not want the board
          // scaling out from under them.
          className="min-h-11 rounded-chip border border-line bg-raised px-3 text-[16px] text-ink"
        />
      </label>

      {people.length === 0 ? (
        <p className="text-[13px] text-ink-muted">Nobody else is in the room.</p>
      ) : shown.length === 0 ? (
        // Named rather than generic: the room is full of people, and "no results"
        // reads as "this is broken" when a host is certain they can see the
        // person on the screen behind this panel.
        <p className="text-[13px] text-ink-muted">Nobody here is called that.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {shown.map((player) => {
            const armed = confirming?.id === player.id ? confirming.action : null;
            return (
              <li
                key={player.id}
                data-player-id={player.id}
                className="flex flex-col gap-1 border-t border-line pt-2"
              >
                <div className="flex items-center gap-2">
                  <span className="truncate text-[15px] font-semibold">{player.displayName}</span>
                  {player.username === null ? null : (
                    <span className="truncate font-mono text-[12px] text-ink-dim">
                      @{player.username}
                    </span>
                  )}
                  {player.role === 'spectator' ? (
                    <span className="ml-auto shrink-0 text-[12px] text-ink-dim">watching</span>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    className={`${row} text-ink hover:bg-raised`}
                    onClick={act({ type: 'mute-player', playerId: player.id, muted: !player.muted })}
                  >
                    <Icon icon={MuteIcon} size={15} />
                    {player.muted ? 'Unmute' : 'Mute'}
                  </button>

                  <button
                    type="button"
                    className={`${row} text-alert hover:bg-alert/15`}
                    onClick={act({ type: 'kick-player', playerId: player.id })}
                  >
                    <Icon icon={KickIcon} size={15} />
                    Kick
                  </button>

                  {armed === 'ban' ? (
                    <button
                      type="button"
                      className={`${row} bg-alert/15 text-alert`}
                      onClick={act({ type: 'ban-player', playerId: player.id })}
                    >
                      <Icon icon={BanIcon} size={15} />
                      Ban, and delete everything they said
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${row} text-alert hover:bg-alert/15`}
                      onClick={() => setConfirming({ id: player.id, action: 'ban' })}
                    >
                      <Icon icon={BanIcon} size={15} />
                      Ban
                    </button>
                  )}

                  {armed === 'purge' ? (
                    <button
                      type="button"
                      className={`${row} bg-alert/15 text-alert`}
                      onClick={act({ type: 'purge-chat', authorId: player.id })}
                    >
                      <Icon icon={TrashIcon} size={15} />
                      Delete every one of them
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${row} text-alert hover:bg-alert/15`}
                      onClick={() => setConfirming({ id: player.id, action: 'purge' })}
                    >
                      <Icon icon={TrashIcon} size={15} />
                      Delete everything they said
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
